import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bReventaService from '@/services/cd2bReventaService'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { cotizar, aplicarMargen, fmtMoneda } from '@/lib/cd2bPricing'
import { useReventaHabilitada, useVendeConMargen } from '@/hooks/useReventaHabilitada'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import BannerGatingReventa from '@/components/cd2b/BannerGatingReventa'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'

const TIPOS_OVERRIDE = [
  { value: 'plan', label: 'Edición (plan)' },
  { value: 'modulo', label: 'Módulo / vertical' },
  { value: 'asiento', label: 'Asiento' },
  { value: 'capacidad', label: 'Paquete de capacidad' },
  { value: 'acceso', label: 'Acceso (app móvil)' },
  { value: 'soporte', label: 'Soporte' },
  { value: 'negocio', label: 'Negocio adicional' },
]

/**
 * Mi margen (docs/120 §7.3): margen global + excepciones por producto + simulador en vivo.
 *
 * El slider/inputs recalculan el simulador EN EL CLIENTE con el espejo `cd2bPricing.js`
 * (`cotizar` + `aplicarMargen`, F1) — cero round-trips mientras el asesor arrastra el slider,
 * igual que el panel de impacto de "Cambiar paquete" en la ficha del cliente recalcula en vivo
 * y solo confirma con el backend al guardar (`putPolitica`).
 */
export default function ReventaMargen() {
  const habilitado = useReventaHabilitada()
  const conMargen = useVendeConMargen()
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [catalogo, setCatalogo] = useState(null)
  const [politica, setPolitica] = useState(null) // la vigente, del backend (para "Descartar")

  // Borrador editable — separado de `politica` para poder "Descartar" sin recargar la página.
  const [margenGlobal, setMargenGlobal] = useState(0)
  const [overrides, setOverrides] = useState([])

  // Ejemplo del simulador ("así se ve tu precio").
  const [edicionEjemplo, setEdicionEjemplo] = useState('')
  const [modulosEjemplo, setModulosEjemplo] = useState([])

  useEffect(() => {
    let vivo = true
    setCargando(true)
    Promise.all([
      cd2bReventaService.getPolitica().catch(() => null),
      cd2bPropuestaService.getCatalogo().catch(() => null),
    ]).then(([pol, cat]) => {
      if (!vivo) return
      const p = pol?.data || null
      setPolitica(p)
      setMargenGlobal(p?.margenGlobalPct ?? 0)
      setOverrides((p?.overrides || []).map((o) => ({ ...o })))
      const c = cat?.data || null
      setCatalogo(c)
      setEdicionEjemplo(c?.ediciones?.find((e) => e.edicion === 'pyme')?.edicion || c?.ediciones?.[0]?.edicion || '')
    }).catch(() => { if (vivo) toast.error('No se pudo cargar tu política de margen') })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
  }, [])

  const tope = politica?.topeVigentePct ?? 0

  const descartar = () => {
    setMargenGlobal(politica?.margenGlobalPct ?? 0)
    setOverrides((politica?.overrides || []).map((o) => ({ ...o })))
    toast('Cambios descartados', { icon: '↺' })
  }

  const publicar = async () => {
    if (margenGlobal > tope) {
      toast.error(`Tu margen global (${margenGlobal}%) supera el tope vigente de tu certificación (${tope}%).`)
      return
    }
    const sinCode = overrides.some((o) => !o.code)
    if (sinCode) { toast.error('Cada excepción necesita un producto seleccionado.'); return }
    setGuardando(true)
    try {
      const res = await cd2bReventaService.putPolitica({ margenGlobalPct: margenGlobal, overrides })
      setPolitica(res?.data || null)
      toast.success('Política de margen publicada. No afecta a clientes ya vendidos.')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar tu política de margen')
    } finally {
      setGuardando(false)
    }
  }

  const agregarOverride = () => {
    const primerModulo = catalogo?.modulos?.[0]
    setOverrides((prev) => [...prev, { tipo: 'modulo', code: primerModulo?.code || '', margenPct: margenGlobal }])
  }
  const quitarOverride = (idx) => setOverrides((prev) => prev.filter((_, i) => i !== idx))
  const cambiarOverride = (idx, patch) => setOverrides((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)))

  const opcionesPorTipo = (tipo) => {
    if (tipo === 'plan') return (catalogo?.ediciones || []).map((e) => ({ code: e.edicion, label: e.label, costo: e.precioBase }))
    if (tipo === 'modulo') return (catalogo?.modulos || []).map((m) => ({ code: m.code, label: m.label, costo: m.precio }))
    return null // asiento/capacidad/acceso/soporte/negocio: code libre (catálogo menos estandarizado en el portal)
  }

  // ── Simulador (100% local, espejo del motor) ────────────────────────────────
  const simulacion = useMemo(() => {
    if (!catalogo?.ediciones?.length) return null
    const cot = cotizar({ edicion: edicionEjemplo, modulos: modulosEjemplo }, catalogo)
    const politicaDraft = { margenGlobalPct: margenGlobal, overrides, topeVigentePct: tope }
    try {
      const m = aplicarMargen(cot, politicaDraft, catalogo)
      return { cot, margen: m }
    } catch (e) {
      return { cot, error: e.message }
    }
  }, [catalogo, edicionEjemplo, modulosEjemplo, margenGlobal, overrides, tope])

  const toggleModuloEjemplo = (code) =>
    setModulosEjemplo((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]))

  // Para la etiqueta "Simulando: ..." — qué se está cotizando ahora mismo, en texto plano.
  const edicionEjemploLabel = catalogo?.ediciones?.find((e) => e.edicion === edicionEjemplo)?.label || ''
  const modulosEjemploLabels = (catalogo?.modulos || [])
    .filter((m) => modulosEjemplo.includes(m.code))
    .map((m) => m.label)

  if (cargando) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </PageContainer>
    )
  }

  const bloqueado = !habilitado || !conMargen

  if (habilitado && !conMargen) {
    return (
      <PageContainer>
        <PageHeader title="Mi margen" description="Cómo ganas con cada venta" />
        <div className="rounded-lg border border-primary-200 dark:border-primary-800 p-5 text-body-sm text-neutral-700 dark:text-neutral-300 space-y-2">
          <p className="font-medium text-neutral-900 dark:text-neutral-50">Ganas por comisión, no por margen.</p>
          <p>Planes y módulos se cotizan al mismo precio que el cliente ve en el portal. Tu ganancia es la comisión
            sobre lo que el cliente paga (sin IVA), más tus propios servicios —implementación, soporte, capacitación—,
            que cotizas como cargos adicionales.</p>
          <p>Un descuento sobre el precio de lista lo aprueba un administrador antes de que puedas enviar la cotización.</p>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader title="Mi margen" description="El precio del catálogo cd-2b es tu costo mayorista. Todo lo que agregues encima es tuyo." />

      {bloqueado && <BannerGatingReventa variante="margen" />}

      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr] items-start">
        <Card className={bloqueado ? 'opacity-60 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="text-h3">Margen global por defecto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={Math.max(tope, 1)}
                  value={margenGlobal}
                  onChange={(e) => setMargenGlobal(Number(e.target.value))}
                  className="w-full accent-accent-500"
                  disabled={bloqueado}
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={margenGlobal}
                  onChange={(e) => setMargenGlobal(Math.max(0, Number(e.target.value) || 0))}
                  className="max-w-24"
                  disabled={bloqueado}
                />
                <span className="text-body-sm text-neutral-500 dark:text-neutral-400">%</span>
              </div>
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-2">
                Tope vigente por tu certificación: <strong>{tope}%</strong>
                {margenGlobal > tope && <span className="text-danger-700 dark:text-danger-300"> · supera el tope, no se podrá publicar</span>}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-body font-semibold text-neutral-900 dark:text-neutral-50">Excepciones por producto</h3>
              </div>
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-3">
                Fija un margen distinto para un módulo o edición específica. Lo que no listes aquí usa el margen global.
              </p>
              <div className="space-y-2">
                {overrides.map((o, idx) => {
                  const opciones = opcionesPorTipo(o.tipo)
                  return (
                    <div key={idx} className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-800 p-2.5">
                      <Select value={o.tipo} onValueChange={(v) => cambiarOverride(idx, { tipo: v, code: '' })} disabled={bloqueado}>
                        <SelectTrigger className="h-10 w-full sm:w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_OVERRIDE.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {opciones ? (
                        <Select value={o.code} onValueChange={(v) => cambiarOverride(idx, { code: v })} disabled={bloqueado}>
                          <SelectTrigger className="h-10 flex-1 min-w-40"><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
                          <SelectContent>
                            {opciones.map((op) => <SelectItem key={op.code} value={op.code}>{op.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          className="flex-1 min-w-40 h-10"
                          placeholder="código del catálogo"
                          value={o.code}
                          onChange={(e) => cambiarOverride(idx, { code: e.target.value })}
                          disabled={bloqueado}
                        />
                      )}
                      <Input
                        type="number" min={0} max={100} className="w-24 h-10"
                        value={o.margenPct ?? ''}
                        onChange={(e) => cambiarOverride(idx, { margenPct: Number(e.target.value) || 0 })}
                        disabled={bloqueado}
                      />
                      <span className="text-body-sm text-neutral-500 dark:text-neutral-400">%</span>
                      <Button variant="ghost" size="icon-sm" className="h-10 w-10" aria-label="Quitar esta excepción" onClick={() => quitarOverride(idx)} disabled={bloqueado}>
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  )
                })}
              </div>
              <Button variant="outline" size="sm" className="mt-3" onClick={agregarOverride} disabled={bloqueado}>
                <Plus size={14} /> Agregar excepción
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-line pt-2">
              <Button onClick={publicar} disabled={bloqueado || guardando}>
                <Save size={15} /> Publicar cambios
              </Button>
              <Button variant="outline" onClick={descartar} disabled={bloqueado || guardando}>
                <RotateCcw size={15} /> Descartar
              </Button>
            </div>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Publicar no afecta a ningún cliente ya vendido — su precio quedó congelado el día de la venta. Solo aplica a cotizaciones nuevas.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <p className="text-caption uppercase tracking-wide text-neutral-400 dark:text-neutral-500">Así se ve tu precio</p>
            <CardTitle className="text-h3">Simulador</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Select value={edicionEjemplo} onValueChange={setEdicionEjemplo}>
                <SelectTrigger><SelectValue placeholder="Edición" /></SelectTrigger>
                <SelectContent>
                  {(catalogo?.ediciones || []).map((e) => <SelectItem key={e.edicion} value={e.edicion}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1.5">
                {(catalogo?.modulos || []).filter((m) => m.optIn).slice(0, 6).map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => toggleModuloEjemplo(m.code)}
                    className={`min-h-9 rounded-full border px-3 py-1 text-caption ${modulosEjemplo.includes(m.code) ? 'bg-accent-100 dark:bg-accent-900/40 border-accent-300 dark:border-accent-700 text-accent-800 dark:text-accent-300' : 'border-line bg-card text-neutral-600 dark:text-neutral-400'}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {simulacion?.error ? (
              <p className="text-body-sm text-danger-700 dark:text-danger-300">{simulacion.error}</p>
            ) : simulacion ? (
              <>
                {/*
                  Ancla explícita antes del precio: sin esto, marcar/desmarcar un módulo mueve el
                  total y es indistinguible de "mi ajuste de margen no se está aplicando" — el %
                  de margen no cambia (se ve abajo), pero el monto en dólares sí, porque ahora se
                  está cotizando MÁS producto. Esta línea deja explícito qué se está cotizando.
                */}
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  Simulando: <strong className="text-neutral-700 dark:text-neutral-300">{edicionEjemploLabel || edicionEjemplo}</strong>
                  {modulosEjemploLabels.length > 0 && <> + {modulosEjemploLabels.join(', ')}</>}
                </p>
                <div className="h-9 rounded-md overflow-hidden flex border border-neutral-200 dark:border-neutral-800 text-caption font-mono">
                  <div
                    className="bg-primary-50 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300 flex items-center justify-center"
                    style={{ flex: Math.max(simulacion.margen.mensual.costo, 0.001) }}
                  >
                    {fmtMoneda(simulacion.margen.mensual.costo)}
                  </div>
                  <div
                    className="bg-accent-100 dark:bg-accent-900/40 text-accent-800 dark:text-accent-300 flex items-center justify-center"
                    style={{ flex: Math.max(simulacion.margen.mensual.margen, 0.001) }}
                  >
                    +{fmtMoneda(simulacion.margen.mensual.margen)}
                  </div>
                </div>
                <div className="flex gap-4 text-caption text-neutral-500 dark:text-neutral-400">
                  <span><span className="inline-block h-2 w-2 rounded-sm bg-primary-100 dark:bg-primary-800 mr-1.5" />Tu costo</span>
                  <span><span className="inline-block h-2 w-2 rounded-sm bg-accent-200 dark:bg-accent-700 mr-1.5" />Tu margen ({margenGlobal}%)</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-line pt-3">
                  <span className="text-body-sm text-neutral-500 dark:text-neutral-400">El cliente paga</span>
                  <span className="font-mono tabular-nums font-semibold text-h3 text-neutral-900 dark:text-neutral-50">{fmtMoneda(simulacion.margen.mensual.venta)} / mes</span>
                </div>
                <p className="text-caption text-neutral-400 dark:text-neutral-500">
                  Esta simulación es solo tuya — el desglose de costo y margen nunca se muestra al cliente.
                </p>
              </>
            ) : (
              <Skeleton className="h-24" />
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
